'use client';
import React, { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { StarIcon } from '@heroicons/react/24/solid';
import { format, parse } from 'date-fns';
import { Product } from 'shared/types/product';
import { useFormat } from 'helpers/hooks/useFormat';
import { getMatchingVariant } from 'helpers/utils/getMatchingVariant';
import { getModuleBackgroundStyle } from 'helpers/utils/helperFunctions';
import { useProduct } from 'frontastic/hooks';
import { Review } from 'frontastic/hooks/useProduct/types';
import Loader from '../../atoms/loader';
import StarRating from '../../atoms/ratings';

export interface ShopsRatingProps {
  dataProps?: {
    modulePlaceholder?: {
      headline?: string;
    };
    productName?: string;
    productSku?: string;
  };
  indexing: number;
}

const ShopsRating: React.FC<ShopsRatingProps> = ({ dataProps, indexing }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { getAllReviewBySku } = useProduct();
  const pathName = usePathname();
  const [productReview, setProductReview] = useState<Review[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortOptionIndex, setSortOptionIndex] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const limit = 3; // Number of reviews per page
  const offset = currentPage * limit;
  const sortOptions = [
    { label: 'Neueste zuerst', compareFn: 'lastModifiedAt desc' },
    { label: 'Niedrig zu hoch', compareFn: 'rating asc' },
    { label: 'Hoch zu niedrig', compareFn: 'rating desc' },
  ];

  const handlePreviousClick = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (productReview?.length === limit) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOptionIndex(parseInt(e.target.value, 10));
    setCurrentPage(0);
  };

  const getReviews = async () => {
    setIsLoading(true);
    if (dataProps?.productSku) {
      try {
        const response = await getAllReviewBySku(
          dataProps?.productSku,
          limit,
          offset,
          sortOptions[sortOptionIndex]?.compareFn,
        );
        setProductReview(response?.reviews?.results || []);
        setTotalReviews(response?.reviews?.total || 0);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getReviews();
  }, [dataProps?.productSku, currentPage, sortOptionIndex]);

  const currentBackgroundStyle = getModuleBackgroundStyle();

  if (isLoading) return <Loader />;
  if (productReview.length === 0) return null;

  return (
    <div
      id="shopsRating"
      className={`mx-auto mb-30 h-fit max-w-screen-xl bg-cover px-10 pb-30 pt-20 lg:px-25 ${
        indexing % 2 === 0 ? currentBackgroundStyle : ''
      }`}
    >
      <div className="flex items-center justify-between">
        <div></div>
        <div className="self-center text-center font-calibri text-24 font-bold text-blueTheme-black">
          {dataProps?.modulePlaceholder?.headline} {dataProps?.productName}
        </div>
        <div>
          <select
            className="border border-solid border-blueTheme-grayLight py-2 text-12 font-bold text-blueTheme-black"
            value={sortOptionIndex}
            onChange={handleSortChange}
          >
            {sortOptions?.map((option, index) => (
              <option key={index} value={index}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        {productReview?.map((ratingData, index) => {
          const trustedJson = ratingData?.custom?.fields?.trustedShopOrder
            ? JSON.parse(ratingData?.custom?.fields?.trustedShopOrder)
            : null;
          const parsedDate = parse(trustedJson?.createdAt, 'dd.MM.yy, HH:mm', new Date());
          const formattedDate = !isNaN(parsedDate.getTime()) ? format(parsedDate, 'dd.MM.yyyy') : null;

          return (
            <div
              className={`${
                index !== productReview.length - 1 && 'border-b-2'
              } border-solid border-blueTheme-white py-20`}
              key={index}
            >
              <div className="flex items-center justify-between">
                <div className="text-16 text-blueTheme-black">{ratingData?.authorName}</div>
                <div>
                  <div className="flex items-center gap-4">
                    {/* {Array.from({ length: Number(ratingData?.rating || '') })?.map((_, index: number) => ( */}
                    <StarRating editable={false} ratingAvg={ratingData?.rating} />
                    {/* ))} */}
                  </div>
                </div>
              </div>
              <div className="text-16 text-blueTheme-black">{formattedDate}</div>
              <div className="text-16 font-bold text-blueTheme-black">{ratingData?.title}</div>
              <div className="text-16 text-blueTheme-black">{ratingData?.text}</div>
            </div>
          );
        })}
      </div>
      {productReview?.length > 0 && (
        <div className="mt-4 flex items-center justify-center">
          {currentPage > 0 && (
            <button onClick={handlePreviousClick} className="px-5 py-1">
              ‹
            </button>
          )}
          <span className="mx-2 font-calibri text-14 font-bold">
            {currentPage + 1} {formatProductMessage({ id: 'of', defaultMessage: 'von' })}{' '}
            {Math.ceil(totalReviews / limit)}
          </span>
          {currentPage + 1 < Math.ceil(totalReviews / limit) && (
            <button onClick={handleNextClick} className="px-5 py-1">
              ›
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ShopsRating;
